/**
 * Schema.org Place nodes for the CAC's physical locations.
 *
 * Built from the `center-locations` Builder model so that adding a location
 * there also adds it to the site's structured data — the alternative was a
 * second hardcoded address every time the organisation opens somewhere.
 *
 * Everything here is pure and string-in/object-out so it can be tested
 * without a Nuxt context.
 */

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAY_INDEX: Record<string, number> = {
    sun: 0, mon: 1, tue: 2, tues: 2, wed: 3, thu: 4, thur: 4, thurs: 4, fri: 5, sat: 6,
};

type CustomHours = { days?: string; hours?: string };
type LocationLike = {
    name?: string;
    description?: string;
    address?: string;
    telephone?: string;
    googleMapLink?: string;
    hoursSource?: string;
    customHours?: CustomHours[];
};

const dayIndexFor = (token: string): number | undefined => {
    const key = token.trim().toLowerCase().replace(/[.]/g, '').slice(0, 5);
    for (const abbr of Object.keys(DAY_INDEX).sort((a, b) => b.length - a.length)) {
        if (key.startsWith(abbr)) return DAY_INDEX[abbr];
    }
    return undefined;
};

/** "Wed - Sat" -> ['Wednesday'...'Saturday']. Wraps the week; [] if unparseable. */
export const parseDayRange = (days?: string): string[] => {
    if (!days) return [];
    const parts = days.split(/\s*[-–—]\s*/);
    const start = dayIndexFor(parts[0] || '');
    if (start === undefined) return [];

    const end = parts.length > 1 ? dayIndexFor(parts[1] || '') : start;
    if (end === undefined) return [];

    const out: string[] = [];
    for (let i = start; ; i = (i + 1) % 7) {
        out.push(DAYS[i]);
        if (i === end || out.length > 7) break;
    }
    return out;
};

const to24Hour = (raw: string): string | null => {
    const m = raw.trim().match(/^(\d{1,2})(?::(\d{2}))?\s*([ap])\.?m\.?$/i);
    if (!m) return null;
    let hour = parseInt(m[1], 10);
    const minute = m[2] || '00';
    const meridiem = m[3].toLowerCase();
    if (hour === 12) hour = 0;
    if (meridiem === 'p') hour += 12;
    return `${String(hour).padStart(2, '0')}:${minute}`;
};

/** "10 AM - 5 PM" -> { opens: '10:00', closes: '17:00' }; null if unparseable. */
export const parseTimeRange = (hours?: string): { opens: string; closes: string } | null => {
    if (!hours) return null;
    const parts = hours.split(/\s*[-–—]\s*/);
    if (parts.length < 2) return null;
    const opens = to24Hour(parts[0]);
    const closes = to24Hour(parts[1]);
    return opens && closes ? { opens, closes } : null;
};

/** "136 S. Dearborn Street, Chicago, IL 60603" -> a PostalAddress. */
export const parseAddress = (address?: string) => {
    if (!address || !address.trim()) return null;

    const cleaned = address.replace(/\s+/g, ' ').trim();
    const zip = cleaned.match(/\b(\d{5})(?:-\d{4})?\b/)?.[1];
    const region = cleaned.match(/\b([A-Z]{2})\b(?=[\s,]*\d{5})/)?.[1];

    // Everything before the first comma is the street; the locality is whatever
    // sits between that and the state, with commas optional around it.
    const [street, ...rest] = cleaned.split(',');
    const tail = rest.join(',');
    const locality = tail
        .replace(new RegExp(`\\b${region}\\b.*$`), '')
        .replace(/[,\s]+$/, '')
        .replace(/^[,\s]+/, '')
        .trim();

    return {
        '@type': 'PostalAddress',
        streetAddress: street.trim(),
        ...(locality ? { addressLocality: locality } : {}),
        ...(region ? { addressRegion: region } : {}),
        ...(zip ? { postalCode: zip } : {}),
        addressCountry: 'US',
    };
};

const SPEC = 'OpeningHoursSpecification';

/**
 * Opening hours for one location. `Center hours` reads the site-wide hours from
 * Global Data (keyed by day name); `Custom hours` reads the location's own rows.
 * Rows that cannot be parsed are dropped rather than emitted half-formed.
 */
export const buildOpeningHours = (location: LocationLike, centerHours?: Record<string, any>) => {
    const source = location?.hoursSource;

    if (source === 'Center hours') {
        if (!centerHours) return [];
        const specs: any[] = [];

        for (const day of DAYS) {
            const entry = centerHours[day.toLowerCase()];
            const opens = entry?.open ? to24Hour(entry.open) : null;
            const closes = entry?.closed ? to24Hour(entry.closed) : null;
            if (!opens || !closes || entry?.centerClosed) continue;

            const last = specs[specs.length - 1];
            if (last && last.opens === opens && last.closes === closes) last.dayOfWeek.push(day);
            else specs.push({ '@type': SPEC, dayOfWeek: [day], opens, closes });
        }
        return specs;
    }

    if (source === 'Custom hours') {
        return (location.customHours || [])
            .map((row) => {
                const dayOfWeek = parseDayRange(row?.days);
                const times = parseTimeRange(row?.hours);
                if (!dayOfWeek.length || !times) return null;
                return { '@type': SPEC, dayOfWeek, ...times };
            })
            .filter(Boolean) as any[];
    }

    return [];
};

/** One Schema.org Place per location, in the order given. */
export const buildLocationPlaces = (locations?: LocationLike[] | null, centerHours?: Record<string, any>) => {
    if (!Array.isArray(locations) || !locations.length) return [];

    return locations
        .map((location) => {
            const address = parseAddress(location?.address);
            if (!address || !location?.name) return null;

            const hours = buildOpeningHours(location, centerHours);

            return {
                '@type': 'Place',
                name: location.name,
                address,
                ...(location.description ? { description: location.description } : {}),
                ...(location.telephone ? { telephone: location.telephone } : {}),
                ...(location.googleMapLink ? { hasMap: location.googleMapLink } : {}),
                ...(hours.length ? { openingHoursSpecification: hours } : {}),
            };
        })
        .filter(Boolean) as any[];
};
